import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDoc, useOrderedCol, useProgress } from '../lib/hooks';
import { useAuthCtx } from '../lib/auth-context';
import { parseYoutubeId, type ChapterDoc, type CourseDoc, type ResourceDoc, type ResourceKind, type SectionDoc } from '../lib/data-model';
import { Loading, ErrorBlock, EmptyState, CheckButton } from '../components/ui';
import { AdSlot } from '../components/AdSlot';
import { useFileUrl } from './public-helpers/use-file-url';
import { kindBadge, resMeta, KIND_ACTION, KIND_LABEL } from './public-helpers/misc';
import { logActivity } from '../lib/study';

export default function ResourcePage() {
  const { resourceId = '' } = useParams();
  const navigate = useNavigate();
  const auth = useAuthCtx();
  const { done, toggleDone, markWatched } = useProgress(auth.isSignedIn);

  const res = useDoc<ResourceDoc>('resources', resourceId);
  const resource = res.data;

  // Video-watch XP: the first open of a video marks it watched (once per video).
  const loggedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (resource?.kind === 'video') {
      markWatched(resource.id);
      if (!loggedRef.current.has(resource.id)) {
        loggedRef.current.add(resource.id);
        logActivity('#2FBF6D', `Watched lecture: ${resource.title}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource?.id, resource?.kind]);
  const needsFile = resource?.kind === 'pdf' || resource?.kind === 'file';
  const file = useFileUrl(needsFile ? resource?.storagePath : undefined, needsFile ? resource?.url : undefined);

  const sectionId = resource?.sectionId ?? '';
  const section = useDoc<SectionDoc>('sections', sectionId, !!sectionId);
  const chapterId = section.data?.chapterId ?? '';
  const chapter = useDoc<ChapterDoc>('chapters', chapterId, !!chapterId);
  const courseId = resource?.courseId ?? chapter.data?.courseId ?? '';
  const course = useDoc<CourseDoc>('courses', courseId, !!courseId);

  // Whole-collection read + client-side filter: composite indexes are not deployed.
  const allRes = useOrderedCol<ResourceDoc>('resources');
  const sectionRes = {
    data: allRes.data.filter(r => r.sectionId === (sectionId || '\u0000')),
    loading: allRes.loading,
    error: allRes.error
  };

  const siblings = useMemo(
    () => sectionRes.data.filter(r => !r.hidden).sort((a, b) => a.order - b.order),
    [sectionRes.data]
  );
  const idx = siblings.findIndex(r => r.id === resourceId);
  const prevId = idx > 0 ? siblings[idx - 1].id : null;
  const nextId = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1].id : null;

  if (res.error) return <ErrorBlock message={res.error} />;
  if (res.loading) return <Loading label="Loading resource…" />;
  if (!resource) {
    return (
      <EmptyState
        glyph="✦"
        title="Resource not found"
        body="This resource may have been removed or unpublished."
        action={<Link className="btn btn-outline" to="/">← Back to chapters</Link>}
      />
    );
  }

  const badge = kindBadge(resource.kind);
  const isDone = done.has(resource.id);
  const ytId = resource.kind === 'video' ? (resource.youtubeId ?? (resource.url ? parseYoutubeId(resource.url) : null)) : null;
  const pdfSrc = resource.kind === 'pdf' ? (resource.storagePath ? file.url : resource.url ?? null) : null;

  const goPrev = () => {
    if (prevId) navigate(`/res/${prevId}`);
    else if (chapterId) navigate(`/ch/${chapterId}`);
  };
  const goNext = () => {
    if (nextId) navigate(`/res/${nextId}`);
    else if (chapterId) navigate(`/ch/${chapterId}`);
  };

  return (
    <div className="page-anim">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden>›</span>
        {course.data && (
          <>
            <Link to={`/c/${course.data.id}`}>{course.data.name}</Link>
            <span aria-hidden>›</span>
          </>
        )}
        {chapter.data && (
          <>
            <Link to={`/ch/${chapter.data.id}`}>{chapter.data.title}</Link>
            <span aria-hidden>›</span>
          </>
        )}
        {section.data && (
          <>
            <Link to={chapterId ? `/ch/${chapterId}` : '/'}>{section.data.title}</Link>
            <span aria-hidden>›</span>
          </>
        )}
        <span>{resource.title}</span>
      </nav>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            <span className="tick" aria-hidden />
            <span style={{ fontFamily: 'var(--font-mono)', letterSpacing: '.1em', fontSize: 11.5 }}>
              {KIND_LABEL[resource.kind]}{section.data ? ` · ${section.data.title}` : ''}
            </span>
          </div>
          <h1 className="page-title" style={{ marginBottom: 6 }}>{resource.title}</h1>
          <div className="mono muted" style={{ fontSize: 12.5 }}>{resMeta(resource.meta, resource.kind)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '12px 16px' }}>
          <CheckButton on={isDone} label={resource.title} onToggle={() => toggleDone(resource.id)} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{isDone ? 'Completed' : 'Mark complete'}</span>
        </div>
      </div>

      <div className="two-col">
        <div className="main">
          {resource.kind === 'video' && ytId && (
            <div className="viewer-chrome">
              <iframe
                className="video-frame"
                title={resource.title}
                src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {resource.kind === 'video' && !ytId && (
            resource.url ? (
              <div className="panel" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span className="res-badge" style={{ background: badge.bg, color: badge.fg }} aria-hidden>{badge.glyph}</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong>{resource.title}</strong>
                  <div className="mono muted" style={{ fontSize: 12, marginTop: 3 }}>{resource.url}</div>
                </div>
                <a className="btn btn-primary" href={resource.url} target="_blank" rel="noopener noreferrer">Watch ↗</a>
              </div>
            ) : (
              <PanelNotice kind={resource.kind} text="This video has no playable link yet. Check back soon." />
            )
          )}

          {resource.kind === 'pdf' && (
            <div className="viewer-chrome">
              <div style={{ padding: '13px 20px', borderBottom: '1px solid var(--line)', background: 'var(--bg)', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 600 }}>{resource.title}</div>
                  <div className="mono muted" style={{ fontSize: 11.5, marginTop: 2 }}>{resMeta(resource.meta, resource.kind)}</div>
                </div>
                {pdfSrc && (
                  <a className="btn btn-outline btn-sm" href={pdfSrc} target="_blank" rel="noopener noreferrer">↓ Download</a>
                )}
              </div>
              {file.status === 'loading' && (
                <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="spinner" aria-hidden />
                </div>
              )}
              {pdfSrc && file.status === 'ready' && (
                <iframe className="pdf-frame" title={resource.title} src={pdfSrc} />
              )}
              {(file.status === 'error' || (!pdfSrc && file.status !== 'loading')) && (
                <div style={{ padding: '46px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: 34, marginBottom: 10 }} aria-hidden>▤</div>
                  <strong>Couldn't open the file preview</strong>
                  <p className="muted" style={{ margin: '8px auto 18px', maxWidth: '46ch', fontSize: 14 }}>
                    The file may be unavailable right now (storage is not configured in this environment).
                  </p>
                  {resource.url && (
                    <a className="btn btn-primary" href={resource.url} target="_blank" rel="noopener noreferrer">
                      Open file ↗
                    </a>
                  )}
                  {!resource.url && (
                    <span className="pill line">Ask the teacher to re-upload the file</span>
                  )}
                </div>
              )}
            </div>
          )}

          {resource.kind === 'file' && (
            <div className="panel" style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <span className="res-badge" style={{ background: badge.bg, color: badge.fg, width: 44, height: 44, borderRadius: 13, fontSize: 18 }} aria-hidden>
                {badge.glyph}
              </span>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 16.5, fontWeight: 600, marginBottom: 3 }}>{resource.fileName ?? resource.title}</div>
                <div className="mono muted" style={{ fontSize: 12 }}>{resMeta(resource.meta, resource.kind)}</div>
              </div>
              {file.url ? (
                <a className="btn btn-primary" href={file.url} target="_blank" rel="noopener noreferrer">
                  {KIND_ACTION[resource.kind]} / Download ↓
                </a>
              ) : file.status === 'loading' ? (
                <button className="btn btn-outline" disabled><span className="spinner" aria-hidden /> Preparing…</button>
              ) : (
                <button className="btn btn-outline" disabled title="File unavailable">Unavailable</button>
              )}
            </div>
          )}

          {resource.kind === 'link' && (
            <div className="panel">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <span className="res-badge" style={{ background: badge.bg, color: badge.fg, width: 40, height: 40, borderRadius: 12, fontSize: 16 }} aria-hidden>
                  {badge.glyph}
                </span>
                <span className="pill line">{KIND_LABEL[resource.kind]}</span>
              </div>
              <h2 style={{ fontFamily: 'var(--font-serif)', fontWeight: 600, fontSize: 22, margin: '0 0 10px' }}>{resource.title}</h2>
              {resource.meta && <p className="lede" style={{ marginBottom: 14 }}>{resource.meta}</p>}
              {resource.url && (
                <p className="mono muted" style={{ fontSize: 12.5, wordBreak: 'break-all', margin: '0 0 18px' }}>{resource.url}</p>
              )}
              {resource.url ? (
                <a className="btn btn-primary btn-lg" href={resource.url} target="_blank" rel="noopener noreferrer">
                  Open resource ↗
                </a>
              ) : (
                <span className="pill warn">No link configured</span>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18, alignItems: 'center' }}>
            <button className="btn btn-outline" onClick={goPrev} disabled={!prevId && !chapterId}>
              ← Previous
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={goNext} disabled={idx < 0 && !chapterId}>
              {nextId ? 'Next resource →' : 'Done — back to chapter →'}
            </button>
          </div>
        </div>

        <aside className="card" style={{ padding: 20, alignSelf: 'flex-start' }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>
            <span className="tick" aria-hidden />
            <span>In this section</span>
          </div>
          {sectionRes.loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink3)' }}>
              <span className="spinner" aria-hidden /><span className="mono" style={{ fontSize: 12.5 }}>Loading…</span>
            </div>
          ) : siblings.length === 0 ? (
            <p className="muted" style={{ fontSize: 14 }}>Nothing else here yet.</p>
          ) : (
            siblings.map((r, i) => {
              const active = r.id === resource.id;
              const b = kindBadge(r.kind);
              return (
                <Link
                  key={r.id}
                  to={`/res/${r.id}`}
                  style={{
                    display: 'flex', gap: 11, alignItems: 'center', padding: '11px 12px', borderRadius: 13,
                    border: `1px solid ${active ? 'var(--brand)' : 'var(--line)'}`,
                    background: active ? 'var(--t-green-soft)' : 'transparent',
                    marginBottom: 9, color: 'inherit', textDecoration: 'none'
                  }}
                >
                  <span style={{ width: 26, height: 26, flex: 'none', borderRadius: 8, background: b.bg, color: b.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }} aria-hidden>
                    {done.has(r.id) ? '✓' : b.glyph}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 500 }}>{r.title}</span>
                    <span className="mono muted" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
                      {i + 1}. {resMeta(r.meta, r.kind)}
                    </span>
                  </span>
                </Link>
              );
            })
          )}
          <AdSlot variant="sidebar" />
        </aside>
      </div>
    </div>
  );
}

function PanelNotice({ kind, text }: { kind: ResourceKind; text: string }) {
  const badge = kindBadge(kind);
  return (
    <div className="panel" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <span className="res-badge" style={{ background: badge.bg, color: badge.fg }} aria-hidden>{badge.glyph}</span>
      <span style={{ color: 'var(--ink2)', fontSize: 14.5 }}>{text}</span>
    </div>
  );
}
